import React, { useRef, useState, useEffect, FormEvent } from "react";
import { TERipple } from "tw-elements-react";
import {
  TEModal,
  TEModalDialog,
  TEModalContent,
  TEModalHeader,
  TEModalBody,
  TEModalFooter,
} from "tw-elements-react";
import PhoneInput from "react-phone-input-2";
import OTPInput from "react-otp-input";
import { FaTelegram, FaWhatsapp } from "react-icons/fa6";
import { FaSms } from "react-icons/fa";
import { BiSolidVideoRecording } from "react-icons/bi";

import EliteLayout from "../../../app-layouts/EliteLayout";
import Preloader from "../../../app-components/preloader/Preloader";
import apiClient from "../../../api-services/ApiClient";

import { successAlert } from "../../../app-util/SweetAlert";
import { useInterest } from "../../../api-services/api-query/Interest/useInterests";
import { useLocation, useNavigate } from "react-router-dom";
import { ELITELINKS } from "../../../app-routes/links";
import { CameraIcon } from "../../../assets/custom-icons";
import { getUser, storeUser } from "../../../api-services/process";
import { UserProfileInterface } from "../../../app-schemas/user.interface";

import ImageCrop from "../../../app-components/image-cropper/ImageCrop";

// ts-fn to handle type Guard
function isLocation(value: any): value is Location {
  return (
    value &&
    typeof value.latitude === "number" &&
    typeof value.longitude === "number"
  );
}

const EliteMakeNewRequest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  const user: UserProfileInterface = getUser();
  const interestData = useInterest();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [useOtherMeans, setUseOtherMeans] = useState(false); // fir OTP notification
  const [isVideo, setIsVideo] = useState(false);

  const [serverError, setServerError] = useState("");
  const [error, setError] = useState("");

  const [resendCode, setResendCode] = useState<boolean>(true);
  const [timeLeft, setTimeLeft] = useState(0);
  const [otp, setOtp] = useState("");
  const [phoneNumber, setPhoneNumber] = useState<string | null>(
    user?.phone_number,
  );

  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [captionImage, setCaptionImge] = useState<any>("");
  const [coordinates, setCoordinates] = useState<any>("");

  const [prespecifiedText, setPrespecifiedText] = useState<string>("");
  const [expectedResponse, setExpectedResponse] = useState<number>(0);
  const [interestId, setInterestId] = useState<number>(
    user?.interests?.[0]?.id ?? 1,
  );

  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoFile, setVideoFile] = useState<any>();
  const [videoLength, setVideoLength] = useState<number | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>("");

  // Use Effect to manage phone KYC verification
  useEffect(() => {
    if (timeLeft === 0) {
      setResendCode(false);
    }

    if (timeLeft > 0) {
      const intervalId = setInterval(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);

      return () => clearInterval(intervalId);
    }
  }, [timeLeft]);

  useEffect(() => {
    handleGeoLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Format time into MM:SS
  const formatTime = (seconds: any) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  // input hander for OPT input
  const handleOTPChange = (otp: any) => {
    setOtp(otp);
  };

  const filterInterest = (data: any) => {
    if (data) {
      const uniqueInterests = new Map();

      data.forEach((item: any) => {
        const interest = item.interest;
        uniqueInterests.set(interest.id, interest);
      });
      const interestsArray = Array.from(uniqueInterests.values());

      return interestsArray;
    }

    return [];
  };

  // Upload Preview icon click
  const handleIconClick = () => {
    fileInputRef.current?.click();
  };

  const isValidImage = (file: File) => {
    const validTypes = ["image/png", "image/jpg", "image/jpeg"];
    return validTypes.includes(file.type);
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];

    if (file && isValidImage(file)) {
      const reader = new FileReader();
      reader.onload = (e) => setPreviewImage(e.target?.result as string);
      reader.readAsDataURL(file);

      await uploadFile(file);
    } else {
      alert("Please select a valid PNG, JPG, or JPEG image.");
      setPreviewImage(null);
    }
  };

  // code to get user current location
  const getUserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        const error = "Geolocation is not supported by this device.";
        setErrorMsg(error);
        return reject(new Error(error));
      }

      if ("permissions" in navigator && navigator.permissions.query) {
        navigator.permissions.query({ name: "geolocation" }).then((status) => {
          if (status.state === "denied") {
            const error =
              "Location access is blocked. Please enable it in your phone browser settings.";
            setErrorMsg(error);
            return reject(new Error(error));
          }

          // Safe to proceed now
          navigator.geolocation.getCurrentPosition(
            (position) => {
              resolve(position.coords);
            },
            (error) => {
              let message = "";
              switch (error.code) {
                case error.PERMISSION_DENIED:
                  message = "User denied the request for Geolocation.";
                  break;
                case error.POSITION_UNAVAILABLE:
                  message = "Location information is unavailable.";
                  break;
                case error.TIMEOUT:
                  message = "The request to get user location timed out.";
                  break;
                default:
                  message = "An unknown error occurred.";
              }

              setErrorMsg(`Error getting location: ${message}`);
              reject(new Error(message));
            },
            {
              enableHighAccuracy: true,
              timeout: 120000,
              maximumAge: 0,
            },
          );
        });
      } else {
        // Permissions API not available, just try geolocation
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve(position.coords);
          },
          (error) => {
            let message = "";
            switch (error.code) {
              case error.PERMISSION_DENIED:
                message = "User denied the request for Geolocation.";
                break;
              case error.POSITION_UNAVAILABLE:
                message = "Location information is unavailable.";
                break;
              case error.TIMEOUT:
                message = "The request to get user location timed out.";
                break;
              default:
                message = "An unknown error occurred.";
            }
            setErrorMsg(
              `Error getting location: ${message} ${JSON.stringify(error)}`,
            );
            reject(new Error(message));
          },
          {
            enableHighAccuracy: true,
            timeout: 120000,
            maximumAge: 0,
          },
        );
      }
    });
  };

  const withTimeout = (promise: Promise<any>, timeout = 120000) =>
    Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Geolocation timed out")), timeout),
      ),
    ]);

  const handleGeoLocation = async () => {
    // e.preventDefault();
    setLoading(true);

    let position;
    try {
      position = await withTimeout(getUserLocation());
    } catch (error) {
      setErrorMsg(
        error instanceof Error
          ? `Error getting your location: ${error.message}`
          : "Unknown error getting your location.",
      );
      setLoading(false);
      return;
    }

    if (isLocation(position)) {
      const coordinates: Location = position;
      setCoordinates(coordinates);
      setLoading(false);

      if (currentPath === ELITELINKS.MAKE_REQUEST_AUTOMATIC) {
        creteAutoRequest(coordinates);
      }
    } else {
      setErrorMsg(
        `Invalid location data received: ${JSON.stringify(position)}`,
      );
    }
  };

  const compressImage = (file: File, quality = 0.9): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          // Set canvas size to desired dimensions (optional resizing)
          const MAX_WIDTH = 1200; // Resize width if image is too large
          const scaleSize = MAX_WIDTH / img.width;
          canvas.width = MAX_WIDTH;
          canvas.height = img.height * scaleSize;

          // Draw the image on canvas
          ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);

          // Compress image to Blob (JPEG with reduced quality)
          canvas.toBlob(
            (blob) => resolve(blob),
            "image/webp", // or "image/webp" for web-friendly compression
            quality, // Compression quality: 0.1 (low) to 1.0 (high)
          );
        };
      };
    });
  };

  const uploadFile = async (file: File) => {
    try {
      const compressedBlob = await compressImage(file, 0.7); // Adjust quality as needed
      if (!compressedBlob) throw new Error("Failed to compress image");
      setUploadProgress(0);

      const formData = new FormData();
      formData.append("gallery_pix", compressedBlob, file.name);

      const res = await apiClient.post("/upload-gallery-pix", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent: any) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            setUploadProgress(percentCompleted - 25);
          }
        },
      });

      setCaptionImge(res.data.file_name);
      setUploadProgress(100);
    } catch (error) {
      console.error(error); // Display error message from apiClient
      setUploadProgress(0); // Reset progress on failure
    }
  };

  const creteNewRequest = async (e: FormEvent) => {
    e.preventDefault();

    if (prespecifiedText === "" || expectedResponse < 1 || !interestId) {
      setServerError("Complete required steps before you submit request");
      return;
    }

    try {
      setLoading(true);
      const data: any = {
        prespecified_text: prespecifiedText,
        expected_response: expectedResponse,
        interest_id: interestId,
        caption_image: captionImage,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
      };

      await apiClient.post("elite-connection-request", data);

      successAlert();
      navigate(ELITELINKS.MY_REQUESTS);
    } catch (error: any) {
      if (error.match("NO PHONE VERIFICATION")) {
        setShowModal(true);
      }

      setServerError(JSON.stringify(error));
    } finally {
      setLoading(false);
    }
  };

  const creteAutoRequest = async (geo_coordinates:any) => {
    try {
      setLoading(true);
      const data: any = {
        prespecified_text: "Hello, I am new here.",
        expected_response: 10,
        interest_id: interestId ?? 1,
        caption_image: captionImage,

        latitude: geo_coordinates ? geo_coordinates.latitude : '',
        longitude: geo_coordinates ? geo_coordinates?.longitude : '',
      };

      await apiClient.post("elite-connection-request", data);

      successAlert();
      navigate(ELITELINKS.MY_REQUESTS);
    } catch (error: any) {
      console.log(error);
      if (error.match("NO PHONE VERIFICATION")) {
        setShowModal(true);
      }

      setServerError(JSON.stringify(error));
    } finally {
      setLoading(false);
    }
  };

  // handle-verifiy phone
  const handleVerification = async (e: FormEvent) => {
    e.preventDefault();
    const result = otp.length === 4;

    setError("");
    if (!result) {
      setError("code must be 6 digit in length");
      return;
    }

    try {
      setLoading(true);
      const data = {
        phone: phoneNumber,
        code: otp,
      };

      const res = await apiClient.post("verify-phone-code", data);

      if (res.data.status) {
        successAlert();
        setShowModal(false);

        const myinfoUpdated = { ...user };
        myinfoUpdated.is_phone_verified = true;

        storeUser(myinfoUpdated);

        if (currentPath === ELITELINKS.MAKE_REQUEST_AUTOMATIC) {
          creteAutoRequest(coordinates);
        } else {
          creteNewRequest(e);
        }
      }
    } catch (error: any) {
      setError(JSON.stringify(error));
    } finally {
      setLoading(false);
    }
  };

  // handle-phone-verification code
  const handleResend2FA = async (type: string) => {
    setError("");
    if (!phoneNumber) {
      setError("Cannot complete process, kindly return to sign in");
      return;
    }

    try {
      setLoading(true);
      setResendCode(true);

      let res = await apiClient.post("request-phone-verification", {
        phoneNumber,
        type,
      });

      if (!!res.data.response?.error) {
        setResendCode(false);
        setError(JSON.stringify(res.data.response.error));
      } else {
        setTimeLeft(180);
      }
    } catch (error: any) {
      setResendCode(false);
      setError(JSON.stringify(error));
    } finally {
      setLoading(false);
    }
  };

  // Upload or process cropped image
  const handleCroppedImage = async (file: File) => {
    setUploadProgress(0);
    await uploadFile(file);
  };

  const handleVideoFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file); // Create a temporary URL for the video
      setVideoUrl(url);
      setVideoFile(file);

      // Create a video element to extract metadata
      const video = document.createElement("video");
      video.src = url;

      video.onloadedmetadata = () => {
        setVideoLength(video.duration);
      };
    }
  };

  const uploadVideo = async () => {
    setUploadProgress(0);
    setUploadLoading(true);
    try {
      // check if video is more than 1 mins
      if (!!videoLength && videoLength > 300)
        throw new Error("Video should be not be more than 30 seconds");

      const formData = new FormData();
      formData.append("gallery_video", videoFile, videoFile.name);

      setUploadProgress(1);
      const res = await apiClient.post("/upload-gallery-video", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent: any) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            setUploadProgress(percentCompleted - 15);
          }
        },
      });

      setUploadLoading(false);
      setCaptionImge(res.data.file_name);
      setUploadProgress(100);
    } catch (error) {
      setUploadLoading(false);
      console.error(error);
      setUploadProgress(0);
    }
  };

  return (
    <EliteLayout title="Make Request">
      <div className="">
        <div className="grid grid-cols-12 gap-8 justify-between w-full">
          <form
            onSubmit={creteNewRequest}
            className="col-span-12 md:col-span-8 lg:col-span-7 bg-hc_F4EBFA dark:bg-slate-800 rounded-10px mt-0.5"
          >
            {/* step 1 */}
            <div className="bg-white dark:bg-slate-800 p-6 mt-16">
              <div>
                <div className="flex gap-1 items-center">
                  <h3 className="text-sm font-semibold dark:text-gray-300">
                    Select your preferred interest
                  </h3>
                  <span className="text-honeConnectOrange text-base font-normal">
                    *
                  </span>
                </div>
                <p className="flex-col gap-3 items-center text-xs font-normal text-honeConnectOrange hidden">
                  Select only one option
                </p>
              </div>

              <div className="grid grid-cols-2 xl:grid-cols-5 gap-y-5 items-center mt-5">
                {filterInterest(interestData.data).map((item: any) => {
                  return (
                    <label
                      key={item.id}
                      className="flex gap-1 items-center xl:col-span-1 custom-checkbox"
                    >
                      <input
                        type="checkbox"
                        name=""
                        id="col1"
                        className="h-5 w-5 accent-purple"
                        onChange={(e) => {
                          setInterestId(item.id);
                        }}
                        checked={item.id === interestId}
                      />
                      <span className="checkmark"></span>
                      <span className="text-sm text-honeConnectGray dark:text-gray-400 font-medium">
                        {item.interest}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* step 2*/}
            <div className="bg-white dark:bg-slate-800 p-6 mt-2">
              <div>
                <div className="flex gap-1 items-center">
                  <h3 className="text-sm font-semibold dark:text-gray-300">
                    What best describes you?
                  </h3>
                  <span className="text-honeConnectOrange text-base font-normal">
                    *
                  </span>
                </div>

                <p className="flex-col gap-3 items-center text-xs font-normal text-honeConnectOrange hidden">
                  Say something about yourself
                </p>
              </div>

              <div className="mt-5">
                <textarea
                  className="w-full xl:w-1/5 bg-honeConnectGray50 dark:bg-slate-800 border border-solid border-honeConnectGray200 dark:border-slate-300 text-gray-600  dark:text-gray-300 p-3.5 py-2 rounded-md focus:outline-1 focus:outline focus:outline-honeConnectPurple font-normal hover:shadow-[#7600bc0a] h-[120px]"
                  onChange={(e: any) => {
                    setPrespecifiedText(e.target.value);
                  }}
                  value={prespecifiedText}
                ></textarea>
              </div>
            </div>

            {/* step 3 */}
            <div className="bg-white dark:bg-slate-800 p-6 mt-2">
              <div className="flex gap-1 items-center">
                <h3 className="text-sm font-semibold dark:text-gray-300">
                  Select number of connects?
                </h3>
                <span className="text-honeConnectOrange text-base font-normal">
                  *
                </span>
              </div>

              <h3 className="flex-col gap-3 items-center text-xs font-normal text-honeConnectOrange hidden">
                Select number of responses
              </h3>

              <div className="flex gap-3 items-center mt-5">
                <h3 className="text-sm font-medium text-honeConnectGray dark:text-gray-300 hidden">
                  Select responses
                </h3>
              </div>

              <select
                className="w-full xl:w-1/5 bg-honeConnectGray50 dark:bg-slate-800 border border-solid border-honeConnectGray200 dark:border-slate-300 text-gray-600  dark:text-gray-300 p-3.5 py-2 rounded-md focus:outline-1 focus:outline focus:outline-honeConnectPurple font-normal hover:shadow-[#7600bc0a]"
                onChange={(e: any) => {
                  setExpectedResponse(e.target.value);
                }}
                value={expectedResponse}
              >
                <option value={0} disabled>
                  Select option
                </option>
                <option value={1}>1 Connect</option>
                <option value={2}>2 Connects</option>
                <option value={3}>3 Connects</option>
                <option value={4}>4 Connects</option>
                <option value={5}>5 Connects</option>
                <option value={6}>6 Connects</option>
                <option value={7}>7 Connects</option>
                <option value={8}>8 Connects</option>
                <option value={9}>9 Connects</option>
                <option value={10}>10 Connects</option>
              </select>
            </div>

            {/* step 4 */}
            <div className="bg-white dark:bg-slate-800 p-6 mt-2 mb-48">
              <div className="flex justify-between gap-10 items-center">
                <div className="">
                  <div className="flex gap-1 items-center">
                    <h3 className="text-sm font-semibold  dark:text-gray-300">
                      {/* Make custom request */}
                      Upload Media
                    </h3>
                    <span className="text-honeConnectOrange text-base font-normal"></span>
                  </div>

                  <p className="flex-col gap-3 items-center text-xs font-normal text-honeConnectOrange hidden">
                    {/* Do you want to specify a different location and request
                    image? */}
                    Your media would be displayed for people who want to connect
                    with you.
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <div className="animate-slow-fade-in">
                  <div className="flex gap-5 justify-center pb-3 text-sm">
                    <button
                      type="button"
                      onClick={() => setIsVideo(false)}
                      className={
                        !isVideo
                          ? "bg-gray-200 dark:text-gray-700 rounded-full px-3 py-1 shadow-sm text-xs"
                          : ""
                      }
                    >
                      Picture
                    </button>
                    <button
                      onClick={() => setIsVideo(true)}
                      type="button"
                      className={
                        isVideo
                          ? "bg-gray-200 dark:text-gray-700 rounded-full px-3 py-1 shadow-sm text-xs"
                          : ""
                      }
                    >
                      Video
                    </button>
                  </div>

                  {/* image area */}
                  {!isVideo && (
                    <div className="flex flex-col text-center justify-center text-sm font-medium">
                      <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept="image/png, image/jpg, image/jpeg" // Restrict file types
                        onChange={handleFileChange}
                      />

                      <div className="flex justify-center mt-2 hidden">
                        <img
                          src={previewImage || CameraIcon} // Show preview if available, otherwise CameraIcon
                          className="w-2/3 p-1 rounded-lg text-center border-2 border-dashed border-honeConnectOrange cursor-pointer"
                          onClick={handleIconClick}
                          alt="Upload Preview"
                        />
                      </div>

                      <ImageCrop onImageSelect={handleCroppedImage} />

                      {/* <p>Upload a picture</p> */}
                      {uploadProgress > 0 && (
                        <div className="flex justify-center items-center gap-1 mt-2">
                          <p>{uploadProgress}%</p>

                          <div
                            className={`h-[4px] flex w-full ${
                              uploadProgress === 100
                                ? "bg-green-700"
                                : "bg-[#ffb7a0]"
                            } rounded-lg`}
                          >
                            <div
                              className={`h-[4px] ${
                                uploadProgress === 100
                                  ? "bg-green-700"
                                  : "bg-honeConnectOrange"
                              } rounded-lg`}
                              style={{ width: uploadProgress + "%" }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* video area */}
                  {isVideo && (
                    <div className="mt-4">
                      <div className="relative w-full xl:w-1/5">
                        <input
                          type="file"
                          accept="video/*"
                          id="videoUpload"
                          onChange={handleVideoFileChange}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        <label htmlFor="videoUpload" className="">
                          <BiSolidVideoRecording className="w-full text-[200px] rounded-lg text-center border-2 border-dashed border-honeConnectOrange cursor-pointer text-honeConnectPurple font-[600]" />
                        </label>
                      </div>

                      {videoUrl && videoLength !== null && (
                        <div>
                          <h2>Video Preview</h2>
                          <video
                            ref={videoRef}
                            controls
                            style={{
                              width: "100%",
                              maxWidth: "600px",
                              margin: "10px 0",
                            }}
                          >
                            <source src={videoUrl} type="video/mp4" />
                            <source src={videoUrl} type="video/webm" />
                            <source src={videoUrl} type="video/ogg" />
                            Your browser does not support the video tag.
                          </video>

                          <p className="text-xs">
                            Video Length: {videoLength.toFixed(2)} seconds
                          </p>

                          {uploadProgress > 0 && (
                            <div className="flex justify-center items-center gap-1 mt-2">
                              <p>{uploadProgress}%</p>

                              <div
                                className={`h-[4px] flex w-full ${
                                  uploadProgress === 100
                                    ? "bg-green-700"
                                    : "bg-[#ffb7a0]"
                                } rounded-lg`}
                              >
                                <div
                                  className={`h-[4px] ${
                                    uploadProgress === 100
                                      ? "bg-green-700"
                                      : "bg-honeConnectOrange"
                                  } rounded-lg`}
                                  style={{ width: uploadProgress + "%" }}
                                ></div>
                              </div>
                            </div>
                          )}

                          <button
                            onClick={uploadVideo}
                            type="button"
                            className="px-3 text-white disabled:text-gray-400 bg-purple-600 disabled:bg-gray-200 text-xs py-1 rounded-sm"
                            disabled={uploadLoading}
                          >
                            {uploadLoading ? "Uploading..." : "Upload Video"}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="fixed z-[2] bottom-16 border-t shadow bg-white dark:bg-slate-800 right-0 left-0 px-6 py-3 dark:border-slate-700 rounded-t-xl">
              <p className="text-red-600 text-[10px] mb-1 xl:w-1/2 text-center z-3">
                {serverError} {errorMsg}
              </p>
              <button
                type="submit"
                className="bg-honeConnectPurple dark:bg-slate-800 dark:text-gray-300 text-gray-100 w-full p-3 rounded-lg border mb-10 shadow-sm font-medium"
              >
                Make a request
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* <!-- Modal --> */}
      <TEModal show={showModal}>
        <TEModalDialog className="rounded-0">
          <TEModalContent className="h-[100vh] overflow-scroll rounded-t-none">
            <TEModalHeader className="bg-honeConnectPurple dark:border-slate-700 dark:bg-slate-800 text-white rounded-t-none">
              {/* <!--Modal title--> */}
              <h5 className="text-md font-normal leading-normal text-white">
                Verify your phone
              </h5>

              <button
                type="button"
                className="box-content rounded-none border-none hover:no-underline hover:opacity-75 focus:opacity-100 focus:shadow-none focus:outline-none"
                onClick={() => setShowModal(false)}
                aria-label="Close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="h-6 w-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </TEModalHeader>

            {/* <!--Modal body--> */}
            <TEModalBody className="dark:bg-slate-800">
              <p className="text-xs mb-5 dark:text-gray-300">
                Phone verification is required to create a request for the first
                time!
              </p>

              <form autoComplete="off" autoCorrect="off" className="space-y-2">
                {/* Phone Number */}
                <div className="flex flex-col w-full mb-1">
                  <label
                    htmlFor="phone"
                    className="mb-1 text-sm font-medium  dark:text-gray-400"
                  >
                    Phone Number
                  </label>
                  <PhoneInput
                    country="ng"
                    value={phoneNumber}
                    onChange={(value) => {
                      setPhoneNumber(value);
                    }}
                    inputClass={`text-xs`}
                    inputProps={{ autoComplete: "off" }}
                  />
                </div>

                <div className="text-xs font-medium text-start mb-16 flex-col justify-center">
                  <span className="mb-3 text-sm dark:text-gray-400">
                    Is this phone correct?{" "}
                  </span>

                  {timeLeft > 0 ? (
                    <button
                      type="button"
                      className="text-honeConnectOrange text-xs font-medium text-center"
                    >
                      {formatTime(timeLeft)}
                    </button>
                  ) : (
                    <div className="flex-col gap-3">
                      <button
                        onClick={() => handleResend2FA("Telegram")}
                        type="button"
                        disabled={resendCode}
                        className="flex gap-2 p-2 px-3 bg-[#27A7E7] rounded mt-3 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-xs font-medium text-center dark:text-gray-200"
                      >
                        <FaTelegram className="text-lg" />
                        <span>Send OTP to Telegram &nbsp;</span>
                      </button>

                      {!useOtherMeans && (
                        <button
                          className="mt-3 text-blue-600 font-medium text-xs"
                          onClick={() => setUseOtherMeans(!useOtherMeans)}
                        >
                          Do not have Telegram? Use other medium.
                        </button>
                      )}

                      {useOtherMeans && (
                        <div className="flex gap-5">
                          <button
                            onClick={() => handleResend2FA("Whatsapp")}
                            type="button"
                            disabled={resendCode}
                            className="flex gap-2 items-center rounded p-2 px-3 bg-green-500 mt-3 text-white  disabled:text-gray-400 disabled:cursor-not-allowed text-honeConnectPurple text-xs font-medium text-center dark:text-gray-200"
                          >
                            <FaWhatsapp className="text-lg" />
                            <span>Send OTP to Whatsapp</span>
                          </button>

                          <button
                            onClick={() => handleResend2FA("SMS")}
                            type="button"
                            disabled={resendCode}
                            className="hidden flex gap-2 items-center rounded bg-blue-500 p-2 px-3 text-white mt-3 disabled:text-gray-400 disabled:cursor-not-allowed text-honeConnectPurple text-xs font-medium text-center dark:text-gray-200"
                          >
                            <FaSms className="text-lg" />
                            <span>Via SMS</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-5 pt-5"></div>

                <div className="flex-col justify-center items-center w-full mt-16">
                  <label
                    htmlFor="phone"
                    className="flex-col mb-1 text-sm font-medium mt-5  dark:text-gray-400"
                  >
                    Enter OTP
                  </label>

                  <OTPInput
                    value={otp}
                    onChange={handleOTPChange}
                    numInputs={4}
                    shouldAutoFocus
                    inputStyle={{
                      width: "3rem",
                      height: "3rem",
                      margin: "0 12px 0 0",
                      fontSize: "20px",
                      borderRadius: 8,
                      border: "1px solid #6d7986",
                      background: "#f8f8f8",
                    }}
                    renderInput={(props) => (
                      <input
                        {...props}
                        type="tel"
                        className="dark:text-slate-800"
                      />
                    )}
                  />
                </div>
              </form>
            </TEModalBody>

            <TEModalFooter className="flex gap-3 dark:bg-slate-800 dark:border-slate-700">
              {error && (
                <p className="flex-col w-full text-red-600 text-[10px] mb-1 xl:w-1/2 text-center">
                  {error}
                </p>
              )}

              <TERipple rippleColor="light" className="">
                <button
                  type="button"
                  className="w-full text-red-700 inline-block rounded bg-red-100 px-6 py-3 text-sm font-medium uppercase leading-normal transition duration-150 ease-in-out hover:bg-primary-accent-100 focus:bg-primary-accent-100 focus:outline-none focus:ring-0 active:bg-primary-accent-200"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
              </TERipple>

              <TERipple rippleColor="light" className="w-2/3">
                <button
                  disabled={otp.length !== 4 || loading}
                  onClick={handleVerification}
                  type="button"
                  className="disabled:bg-gray-400 w-full inline-block rounded px-6 py-3 text-sm font-medium uppercase text-gray-500 bg-honeConnectPurple  dark:bg-slate-800 border dark:border-slate-300 text-white shadow dark:text-gray-300"
                >
                  {loading ? "Verifying" : "Verify"}
                </button>
              </TERipple>
            </TEModalFooter>
          </TEModalContent>
        </TEModalDialog>
      </TEModal>

      <Preloader loading={loading} />
    </EliteLayout>
  );
};

export default EliteMakeNewRequest;
